// src/context/ChatContext.jsx — Real-time Socket.IO, Team Chat & Buddy State
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { io } from "socket.io-client";
import api from "../services/api";
import { useAuth } from "./AuthContext";
import toast from "react-hot-toast";

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const socketRef = useRef(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isDockOpen, setIsDockOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("chat"); // 'chat' | 'buddy' | 'reminders'

  // Channels & Messages
  const [channels, setChannels] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [typingMap, setTypingMap] = useState({}); // channelId -> Array<{ userId, userName }>

  // Preferences & Settings
  const [userPrefs, setUserPrefs] = useState({ roast_opt_in: false, bot_dms_enabled: true });
  const [botSettings, setBotSettings] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("crm_chat_sound") === "true");

  // Buddy state
  const [buddySettings, setBuddySettings] = useState(null);
  const [buddyMessages, setBuddyMessages] = useState([]);
  const [buddyPendingActions, setBuddyPendingActions] = useState([]);
  const [buddyLoading, setBuddyLoading] = useState(false);

  // Reminders state
  const [unifiedReminders, setUnifiedReminders] = useState([]);

  // References to avoid re-triggering socket lifecycle on layout/tab state changes
  const activeChannelIdRef = useRef(activeChannelId);
  activeChannelIdRef.current = activeChannelId;

  const isDockOpenRef = useRef(isDockOpen);
  isDockOpenRef.current = isDockOpen;

  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  const soundEnabledRef = useRef(soundEnabled);
  soundEnabledRef.current = soundEnabled;

  // Calculate total unread count
  const unreadTotal = channels.reduce((sum, ch) => sum + (Number(ch.unreadCount) || 0), 0);

  // Play gentle notification sound
  const playChatNotification = useCallback(() => {
    if (!soundEnabledRef.current) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(659.25, now); // E5
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.08, now + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.3);
    } catch (_) {}
  }, []);

  // ── 1. Fetch Initial Data ─────────────────────────────────────
  const loadChannels = useCallback(async () => {
    if (!user || window.location.pathname === "/onboarding" || window.location.pathname === "/") return;
    try {
      const { data } = await api.get("/chat/channels");
      const list = Array.isArray(data) ? data : [];
      setChannels(list);
      if (!activeChannelIdRef.current && list.length > 0) {
        setActiveChannelId(list[0].id);
      }
    } catch (err) {
      // Silently ignore during onboarding/auth transitions
      if (err?.response?.status !== 403) {
        console.error("[Chat] Failed to load channels:", err);
      }
    }
  }, [user]);

  const loadUserPrefs = useCallback(async () => {
    if (!user || window.location.pathname === "/onboarding" || window.location.pathname === "/") return;
    try {
      const { data } = await api.get("/chat/prefs");
      if (data) setUserPrefs(data);
    } catch (_) {}
  }, [user]);

  const loadBotSettings = useCallback(async () => {
    if (!user || user.role !== "Admin" || window.location.pathname === "/onboarding" || window.location.pathname === "/") return;
    try {
      const { data } = await api.get("/chat/bot/settings");
      if (data) setBotSettings(data);
    } catch (_) {}
  }, [user]);

  const loadBuddySettings = useCallback(async () => {
    if (!user || window.location.pathname === "/onboarding" || window.location.pathname === "/") return;
    try {
      const { data } = await api.get("/buddy/settings");
      if (data) setBuddySettings(data);
    } catch (_) {}
  }, [user]);

  const loadBuddyHistory = useCallback(async () => {
    if (!user || window.location.pathname === "/onboarding" || window.location.pathname === "/") return;
    try {
      setBuddyLoading(true);
      const { data } = await api.get("/buddy/messages");
      if (data) {
        const msgs = Array.isArray(data) ? data : (Array.isArray(data.messages) ? data.messages : []);
        setBuddyMessages(msgs);
        try {
          sessionStorage.setItem(`crm_buddy_msgs_${user.id}`, JSON.stringify(msgs));
        } catch (_) {}
        setBuddyPendingActions(Array.isArray(data.pendingActions) ? data.pendingActions : []);
      }
    } catch (err) {
      console.error("[Buddy] Failed to load messages:", err);
    } finally {
      setBuddyLoading(false);
    }
  }, [user]);

  const loadUnifiedReminders = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/reminders/unified");
      setUnifiedReminders(Array.isArray(data) ? data : []);
    } catch (_) {}
  }, [user]);

  // Load message feed for active channel
  const loadMessages = useCallback(async (channelId, beforeId = null) => {
    if (!channelId) return;
    try {
      setLoadingMessages(true);
      const params = { limit: 50 };
      if (beforeId) params.before = beforeId;

      const { data } = await api.get(`/chat/channels/${channelId}/messages`, { params });
      // Correctly handle both array directly or { messages: [...] }
      const newBatch = Array.isArray(data) ? data : (Array.isArray(data?.messages) ? data.messages : []);

      if (beforeId) {
        setMessages(prev => [...newBatch, ...prev]);
      } else {
        setMessages(newBatch);
        try {
          sessionStorage.setItem(`crm_chat_msgs_${channelId}`, JSON.stringify(newBatch));
        } catch (_) {}
      }
      setHasMoreMessages(newBatch.length === 50);

      // Auto-mark read if dock is open and looking at this channel
      if (isDockOpenRef.current && activeTabRef.current === "chat") {
        api.post(`/chat/channels/${channelId}/read`).catch(() => {});
        setChannels(prev => prev.map(c => c.id === channelId ? { ...c, unreadCount: 0 } : c));
      }
    } catch (err) {
      console.error("[Chat] Failed to load messages:", err);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  // ── 2. Socket.IO Lifecycle (Persistent — does not reconnect on dock open/close) ─
  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const token = localStorage.getItem("accessToken");
    if (!token) return;

    // Use current origin so it connects through Nginx / Cloudflare correctly
    const socket = io(window.location.origin, {
      path: "/socket.io",
      auth: { token },
      transports: ["polling", "websocket"],
      reconnectionAttempts: 10,
      reconnectionDelay: 1000
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setIsConnected(true);
      setIsReconnecting(false);
      loadChannels();
    });

    socket.on("reconnect_attempt", () => {
      setIsReconnecting(true);
    });

    socket.on("reconnect", () => {
      setIsConnected(true);
      setIsReconnecting(false);
      loadChannels();
      if (activeChannelIdRef.current) {
        loadMessages(activeChannelIdRef.current);
      }
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
    });

    // Real-time message receiver
    socket.on("message:new", (msg) => {
      const currentActiveId = activeChannelIdRef.current;
      const isOpen = isDockOpenRef.current;
      const currentTab = activeTabRef.current;

      // Append to active channel feed
      if (msg.channel_id === currentActiveId) {
        setMessages(prev => {
          if (prev.some(m => m.id === msg.id)) return prev;
          const updated = [...prev, msg];
          try {
            sessionStorage.setItem(`crm_chat_msgs_${currentActiveId}`, JSON.stringify(updated));
          } catch (_) {}
          return updated;
        });

        // If dock is open and looking at this channel, auto-mark read
        if (isOpen && currentTab === "chat") {
          api.post(`/chat/channels/${msg.channel_id}/read`).catch(() => {});
        }
      }

      // Update channels list last message & unread badge
      setChannels(prev => prev.map(c => {
        if (c.id === msg.channel_id) {
          const isCurrentActive = c.id === currentActiveId && isOpen && currentTab === "chat";
          return {
            ...c,
            lastMessageBody: msg.body,
            lastMessageAt: msg.created_at,
            unreadCount: isCurrentActive ? 0 : (Number(c.unreadCount) || 0) + 1
          };
        }
        return c;
      }));

      // Play sound and show toast if not active or dock collapsed
      if (!isOpen || msg.channel_id !== currentActiveId) {
        playChatNotification();
        if (msg.is_bot) {
          toast(`PlatinumBot: ${msg.body.slice(0, 60)}...`, { icon: "🤖" });
        } else if (msg.senderName) {
          toast(`${msg.senderName}: ${msg.body.slice(0, 60)}...`);
        }
      }
    });

    socket.on("message:edited", ({ id, body, edited_at }) => {
      setMessages(prev => prev.map(m => m.id === id ? { ...m, body, edited_at } : m));
    });

    socket.on("message:deleted", ({ id }) => {
      setMessages(prev => prev.map(m => m.id === id ? { ...m, deleted_at: new Date().toISOString() } : m));
    });

    socket.on("reaction:update", ({ messageId, reactions }) => {
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions } : m));
    });

    socket.on("typing:update", ({ channelId, userId: tUid, userName, isTyping }) => {
      if (tUid === user.id) return;
      setTypingMap(prev => {
        const cur = prev[channelId] || [];
        const next = isTyping 
          ? [...cur.filter(u => u.userId !== tUid), { userId: tUid, userName }]
          : cur.filter(u => u.userId !== tUid);
        return { ...prev, [channelId]: next };
      });
    });

    // Reminder due notification
    socket.on("reminder:due", (rem) => {
      playChatNotification();
      toast(`⏰ Reminder: ${rem.title}`, { duration: 6000 });
      loadUnifiedReminders();
    });

    // Staff status alert (Exclusively for Team Managers, Admin never alerted)
    socket.on("staff:status_alert", (alert) => {
      if (user?.role !== "Manager") return;
      const icon = alert.status === 'break' ? '☕' : alert.status === 'online' ? '🟢' : alert.status === 'meeting' ? '👥' : '⏸️';
      toast(alert.message || `Staff update: ${alert.name}`, {
        icon,
        duration: 4500,
        style: {
          borderRadius: '10px',
          background: 'var(--surface, #1e293b)',
          color: 'var(--text, #fff)',
          border: '1px solid var(--border, #334155)',
          fontSize: '13px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
        }
      });
    });

    // Channel created notification
    socket.on("channel:created", (newChan) => {
      setChannels(prev => {
        if (prev.some(c => c.id === newChan.id)) return prev;
        return [...prev, newChan];
      });
    });

    // Channel deleted notification
    socket.on("channel:deleted", ({ channelId }) => {
      setChannels(prev => prev.filter(c => c.id !== channelId));
      if (activeChannelIdRef.current === channelId) {
        setActiveChannelId(1);
      }
      try {
        sessionStorage.removeItem(`crm_chat_msgs_${channelId}`);
      } catch (_) {}
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, loadChannels, loadMessages, playChatNotification, loadUnifiedReminders]);

  // Initial fetch on login
  useEffect(() => {
    if (!user) return;
    // Hydrate Buddy messages from sessionStorage instantly
    try {
      const cachedBuddy = sessionStorage.getItem(`crm_buddy_msgs_${user.id}`);
      if (cachedBuddy) {
        const parsed = JSON.parse(cachedBuddy);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setBuddyMessages(parsed);
        }
      }
    } catch (_) {}

    loadChannels();
    loadUserPrefs();
    loadBotSettings();
    loadBuddySettings();
    loadBuddyHistory();
    loadUnifiedReminders();
  }, [user, loadChannels, loadUserPrefs, loadBotSettings, loadBuddySettings, loadBuddyHistory, loadUnifiedReminders]);

  // Reload messages when active channel changes (with instant cache hydration)
  useEffect(() => {
    if (activeChannelId) {
      try {
        const cached = sessionStorage.getItem(`crm_chat_msgs_${activeChannelId}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setMessages(parsed);
          }
        }
      } catch (_) {}
      loadMessages(activeChannelId);
    }
  }, [activeChannelId, loadMessages]);

  // Refresh active tab data when dock opens or active tab switches
  useEffect(() => {
    if (isDockOpen) {
      if (activeTab === "chat" && activeChannelId) {
        loadMessages(activeChannelId);
      } else if (activeTab === "buddy") {
        loadBuddyHistory();
      } else if (activeTab === "reminders") {
        loadUnifiedReminders();
      }
    }
  }, [isDockOpen, activeTab, activeChannelId, loadMessages, loadBuddyHistory, loadUnifiedReminders]);

  // ── Actions ──────────────────────────────────────────────────
  const sendMessage = async (body, replyToId = null) => {
    if (!activeChannelId || !body.trim()) return;
    try {
      const { data } = await api.post(`/chat/channels/${activeChannelId}/messages`, {
        body: body.trim(),
        replyToId
      });
      // Fallback local append if socket has not yet echoed
      setMessages(prev => {
        if (prev.some(m => m.id === data.id)) return prev;
        return [...prev, data];
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send message");
    }
  };

  const toggleReaction = async (messageId, reactionKey) => {
    try {
      await api.post(`/chat/messages/${messageId}/reactions`, { reactionKey });
    } catch (_) {}
  };

  const deleteMessage = async (messageId) => {
    try {
      await api.delete(`/chat/messages/${messageId}`);
    } catch (err) {
      toast.error("Failed to delete message");
    }
  };

  const editMessage = async (messageId, newBody) => {
    try {
      await api.patch(`/chat/messages/${messageId}`, { body: newBody });
    } catch (err) {
      toast.error("Failed to edit message");
    }
  };

  const startTyping = (channelId) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit("typing:start", { channelId });
    }
  };

  const stopTyping = (channelId) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit("typing:stop", { channelId });
    }
  };

  const createChannel = async (name, type = "public", memberIds = []) => {
    try {
      const { data } = await api.post("/chat/channels", { name, type, memberIds });
      await loadChannels();
      setActiveChannelId(data.id);
      toast.success(`Channel #${data.name} created!`);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create channel");
      throw err;
    }
  };

  const deleteChannel = async (channelId) => {
    try {
      await api.delete(`/chat/channels/${channelId}`);
      setChannels(prev => prev.filter(c => c.id !== channelId));
      if (activeChannelId === channelId) {
        setActiveChannelId(1);
      }
      try {
        sessionStorage.removeItem(`crm_chat_msgs_${channelId}`);
      } catch (_) {}
      toast.success("Channel deleted");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete channel");
      throw err;
    }
  };

  const startDirectMessage = async (targetUserId) => {
    try {
      const { data } = await api.post("/chat/dm", { targetUserId });
      await loadChannels();
      setActiveChannelId(data.id);
      setActiveTab("chat");
      setIsDockOpen(true);
      return data;
    } catch (err) {
      toast.error("Failed to start direct message");
    }
  };

  const updatePrefs = async (newPrefs) => {
    try {
      const { data } = await api.put("/chat/prefs", newPrefs);
      setUserPrefs(data);
      toast.success("Preferences saved!");
    } catch (err) {
      toast.error("Failed to save preferences");
    }
  };

  const updateBotConfig = async (newConfig) => {
    try {
      const { data } = await api.put("/chat/bot/settings", newConfig);
      setBotSettings(data);
      toast.success("PlatinumBot settings updated!");
    } catch (err) {
      toast.error("Failed to update bot settings");
    }
  };

  // Buddy Assistant Actions
  const sendBuddyMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = {
      id: Date.now(),
      role: "user",
      body: text.trim(),
      created_at: new Date().toISOString()
    };
    setBuddyMessages(prev => {
      const updated = [...prev, userMsg];
      if (user) {
        try { sessionStorage.setItem(`crm_buddy_msgs_${user.id}`, JSON.stringify(updated)); } catch (_) {}
      }
      return updated;
    });
    setBuddyLoading(true);

    try {
      const { data } = await api.post("/buddy/message", { message: text.trim() });
      if (data.reply) {
        setBuddyMessages(prev => {
          const updated = [
            ...prev,
            {
              id: Date.now() + 1,
              role: "buddy",
              body: data.reply,
              created_at: new Date().toISOString()
            }
          ];
          if (user) {
            try { sessionStorage.setItem(`crm_buddy_msgs_${user.id}`, JSON.stringify(updated)); } catch (_) {}
          }
          return updated;
        });
      }
      if (data.pendingAction) {
        setBuddyPendingActions(prev => [data.pendingAction, ...prev]);
      }
    } catch (err) {
      toast.error("Buddy failed to respond");
    } finally {
      setBuddyLoading(false);
    }
  };

  const confirmBuddyAction = async (actionId) => {
    try {
      const { data } = await api.post(`/buddy/actions/${actionId}/confirm`);
      setBuddyPendingActions(prev => prev.filter(a => a.id !== actionId));
      toast.success("Action confirmed and executed!");
      loadBuddyHistory();
      loadUnifiedReminders();
      return data;
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to confirm action");
    }
  };

  const rejectBuddyAction = async (actionId) => {
    try {
      await api.post(`/buddy/actions/${actionId}/reject`);
      setBuddyPendingActions(prev => prev.filter(a => a.id !== actionId));
      toast("Action cancelled");
    } catch (_) {}
  };

  const clearBuddyHistory = async () => {
    try {
      await api.delete("/buddy/data");
      setBuddyMessages([]);
      setBuddyPendingActions([]);
      toast.success("Buddy chat history cleared");
    } catch (err) {
      toast.error("Failed to clear Buddy history");
    }
  };

  const updateBuddyConfig = async (newSettings) => {
    try {
      const { data } = await api.put("/buddy/settings", newSettings);
      setBuddySettings(data);
      toast.success("Buddy settings updated!");
    } catch (err) {
      toast.error("Failed to update Buddy settings");
    }
  };

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem("crm_chat_sound", String(next));
      return next;
    });
  };

  return (
    <ChatContext.Provider
      value={{
        // Connection & Layout
        isConnected,
        isReconnecting,
        isDockOpen,
        setIsDockOpen,
        activeTab,
        setActiveTab,
        unreadTotal,
        soundEnabled,
        toggleSound,

        // Channels & Chat
        channels,
        activeChannelId,
        setActiveChannelId,
        messages,
        hasMoreMessages,
        loadingMessages,
        loadMessages,
        loadChannels,
        typingUsers: typingMap[activeChannelId] || [],
        sendMessage,
        toggleReaction,
        deleteMessage,
        editMessage,
        startTyping,
        stopTyping,
        createChannel,
        deleteChannel,
        startDirectMessage,

        // Preferences & Admin
        userPrefs,
        updatePrefs,
        botSettings,
        updateBotConfig,

        // Buddy Assistant
        buddySettings,
        buddyMessages,
        buddyPendingActions,
        buddyLoading,
        sendBuddyMessage,
        confirmBuddyAction,
        rejectBuddyAction,
        clearBuddyHistory,
        updateBuddyConfig,

        // Unified Reminders
        unifiedReminders,
        loadUnifiedReminders
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
